import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
} from 'react';

import { ActionMap } from '@src/../types';

export const searchTags = ['Tags', 'Files', 'Check Ins'] as const;
type SearchTag = (typeof searchTags)[number];

enum SearchActionType {
  setSearch,
  toggleFilter,
  setIsSearching,
  setIsSearchPreview,
  markMessageUnavailable,
}

type SearchStatePayload = {
  [SearchActionType.setSearch]: string;
  [SearchActionType.toggleFilter]: SearchTag;
  [SearchActionType.setIsSearching]: boolean;
  [SearchActionType.setIsSearchPreview]: boolean;
  [SearchActionType.markMessageUnavailable]: string;
};

type SearchState = {
  search: string;
  filter: SearchTag[];
  isSearching: boolean;
  isSearchPreview: boolean;
  // TA-11078: ids of search results whose message turned out to be deleted.
  unavailableMessageIds: string[];
};

type SearchContext = SearchState & {
  setSearch: (search: SearchState['search']) => void;
  toggleFilter: (filter: SearchState['filter'][number]) => void;
  setIsSearching: (isSearching: SearchState['isSearching']) => void;
  setIsSearchPreview: (isSearchPreview: boolean) => void;
  markMessageUnavailable: (messageId: string) => void;
};

const initialSearchState: SearchState = {
  search: '',
  filter: [],
  isSearching: false,
  isSearchPreview: false,
  unavailableMessageIds: [],
};

type SearchActions =
  ActionMap<SearchStatePayload>[keyof ActionMap<SearchStatePayload>];
const reducer = (state: SearchState, action: SearchActions): SearchState => {
  switch (action.type) {
    case SearchActionType.setSearch:
      return {
        ...state,
        isSearching: true,
        search: action.payload,
      };
    case SearchActionType.toggleFilter: {
      return {
        ...state,
        isSearching: true,
        filter: [action.payload],
      };
    }
    case SearchActionType.setIsSearching: {
      if (action.payload) {
        return { ...state, isSearching: true };
      } else {
        return {
          ...state,
          isSearching: false,
          search: '',
          filter: [],
        };
      }
    }
    case SearchActionType.setIsSearchPreview: {
      return {
        ...state,
        isSearchPreview: action.payload,
      };
    }
    case SearchActionType.markMessageUnavailable: {
      if (state.unavailableMessageIds.includes(action.payload)) return state;
      return {
        ...state,
        unavailableMessageIds: [...state.unavailableMessageIds, action.payload],
      };
    }
    default:
      return state;
  }
};

const searchContext = createContext<SearchContext | undefined>(undefined);

export const SearchProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { Provider } = searchContext;
  const [state, dispatch] = useReducer(reducer, initialSearchState);

  const setSearch = useCallback((search: typeof state.search) => {
    dispatch({ type: SearchActionType.setSearch, payload: search });
  }, []);

  const toggleFilter = useCallback((filter: (typeof state.filter)[number]) => {
    dispatch({ type: SearchActionType.toggleFilter, payload: filter });
  }, []);

  const setIsSearching = useCallback(
    (isSearching: typeof state.isSearching) => {
      dispatch({ type: SearchActionType.setIsSearching, payload: isSearching });
    },
    []
  );

  const setIsSearchPreview = useCallback((isSearchPreview: boolean) => {
    dispatch({
      type: SearchActionType.setIsSearchPreview,
      payload: isSearchPreview,
    });
  }, []);

  const markMessageUnavailable = useCallback((messageId: string) => {
    dispatch({
      type: SearchActionType.markMessageUnavailable,
      payload: messageId,
    });
  }, []);

  return (
    <Provider
      value={{
        ...state,
        setSearch,
        toggleFilter,
        setIsSearching,
        setIsSearchPreview,
        markMessageUnavailable,
      }}>
      {children}
    </Provider>
  );
};

const useSearch = (): SearchContext => {
  const context = useContext(searchContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a Provider');
  }
  return context;
};

export default useSearch;
